<template>
  <div v-if="isOpen" class="dialog-overlay" @click="handleCancel()">
    <div class="dialog-content" @click.stop>
      <div class="dialog-header">
        <h2>Move Training Sessions</h2>
        <button class="close-btn" @click="handleCancel()">✕</button>
      </div>

      <div class="dialog-body">
        <div class="section">

              <div class="section">
          <h3>Move to Experiment</h3>
          <select v-model="selectedTrainer" class="trainer-select">
            <option :value="null" disabled>Select a trainer...</option>
            <option v-for="trainer in availableTrainers" :key="trainer.id" :value="trainer">
              {{ trainer.name }}
            </option>
          </select>
        </div>

          <h3>Selected Sessions</h3>
          <div class="sessions-list">
            <div v-for="session in selectedSessions" :key="session.id" class="session-item">
              <div class="session-id">{{ toHex(session.id) }}</div>
              <div class="session-name">{{ session.name || 'Untitled' }}</div>
            </div>
          </div>
        </div>

      
      </div>

      <div class="dialog-footer">
        <button class="dialog-button dialog-button-cancel" @click="handleCancel()">
          Cancel
        </button>
        <button
          class="dialog-button dialog-button-move"
          :disabled="!selectedTrainer || selectedSessions.length === 0"
          @click="handleMove()"
        >
          Move
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

const store = useStore();

const props = defineProps({
  selectedSessions: {
    type: Array,
    default: () => [],
  },
  currentTrainerId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['cancelled', 'move-sessions']);

const isOpen = ref(false);
const selectedTrainer = ref(null);
const experimentList = ref([]);

const toHex = (id) => {
  return "t-" + id.toString(16);
};


const availableTrainers = computed(() => {
  if (!experimentList.value || !Array.isArray(experimentList.value)) {
    return [];
  }
  return experimentList.value.filter((trainer) => trainer.id !== props.currentTrainerId);
});

const getExperimentList = async () => {
  try {
    const projectID = store.state.projects.currentProject?.id;
    
    if (!projectID) {
      console.warn('Project ID is required to fetch experiments');
      return;
    }

    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.getExperimentList({
      project_id: projectID,
    });

    if (response && response.result) {
      experimentList.value = response.result;
    } else {
      console.error('Failed to fetch experiments:', response?.error);
    }
  } catch (error) {
    console.error('Error fetching experiments:', error);
  }
};

const openModal = () => {
  isOpen.value = true;
  selectedTrainer.value = null;
  getExperimentList();
};

const closeModal = () => {
  isOpen.value = false;
  selectedTrainer.value = null;
};

const handleCancel = () => {
  closeModal();
  emit('cancelled');
};

const handleMove = async () => {
  if (!selectedTrainer.value) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'Please select a trainer/experiment to move to',
      type: 'warning',
      position: 'top',
    });
    return;
  }
  
  if (props.selectedSessions.length === 0) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: 'No sessions selected to move',
      type: 'warning',
      position: 'top',
    });
    return;
  }
  
  try {
    const sessionIds = props.selectedSessions.map(session => session.id);
    const expId = selectedTrainer.value.id;
    
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api("trainer.session.move", {
      session_ids: sessionIds,
      exp_id: expId,
    });

    if (response && !response.error) {
      emit('move-sessions', {
        sessions: props.selectedSessions,
        targetTrainer: selectedTrainer.value,
        success: true,
      });
      closeModal();
    } else {
      const errorMessage = response?.error?.message || 'Failed to move sessions';
      console.error('Failed to move sessions:', errorMessage);
      $toast.open({
        queue: true,
        dismissible: true,
        message: errorMessage,
        type: 'error',
        position: 'top',
      });
    }
  } catch (error) {
    const errorMessage = error?.message || 'Error moving sessions';
    console.error('Error moving sessions:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  }
};

onMounted(() => {
  // Experiments will be fetched when dialog opens
});

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.dialog-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #f5f5f5;
      border-radius: 4px;
    }
  }
}

.dialog-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  h3 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
  }
}

.sessions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 13px;

  .session-id {
    font-weight: 600;
    color: #666;
    min-width: 80px;
  }

  .session-name {
    flex: 1;
    color: #333;
  }
}

.trainer-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb), 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
}
</style>
